import { useState, useEffect } from "react"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import { GetCMSContent } from "aldoo-ra/CMS/api"
import { DEFAULT_THEME_ID, ACCOUNT_DEFAULT_ID } from "./constants"

// Cache the resolved sample image so every System Default thumbnail on the
// page shares one network round-trip (gallery / dropdown / drawer all hit
// this hook concurrently). Promise so concurrent callers await the same
// fetch instead of firing duplicate requests.
let _systemDefaultBgPromise = null
function getSystemDefaultBg() {
  if (!_systemDefaultBgPromise) {
    _systemDefaultBgPromise = GetCMSContent({
      match: { 'content.type': 'ImageGallery', 'content.id': 'share_backgrounds' },
    })
      .then((res) => {
        const images = res?.data?.[0]?.content?.images
        if (!Array.isArray(images) || !images.length) return null
        return cmsUrl(images[0])
      })
      .catch(() => null)
  }
  return _systemDefaultBgPromise
}

// Resolve a theme's background image (the field may hold a single ref or an
// array of refs depending on the editor) to a usable URL, or null.
export function themeBgUrl(backgroundImage) {
  if (!backgroundImage) return null
  const ref = Array.isArray(backgroundImage) ? backgroundImage[0] : backgroundImage
  if (!ref) return null
  try {
    return cmsUrl(ref)
  } catch {
    return null
  }
}

// Visual thumbnail of a theme: the background image if set, otherwise a
// gradient built from the theme's background/primary colors. A small "Download"
// pill previews the primary/text colors the recipient would see.
export default function ThemeThumbnail({ theme, className = "" }) {
  const [imgError, setImgError] = useState(false)
  const c = theme?.content || theme || {}
  // Both virtual options need the same fallback when no image is set:
  // System Default never has one, and Account Default inherits from the
  // user's gallery default — which can also be the System Default itself.
  // Pull a sample from share_backgrounds in that case so the row isn't a
  // blank gradient.
  const isVirtual = theme?._id === DEFAULT_THEME_ID || theme?._id === ACCOUNT_DEFAULT_ID
  const [systemBg, setSystemBg] = useState(null)
  useEffect(() => {
    if (!isVirtual || c.background_image) return
    let alive = true
    getSystemDefaultBg().then((url) => { if (alive) setSystemBg(url) })
    return () => { alive = false }
  }, [isVirtual, c.background_image])
  const bg = !imgError ? (themeBgUrl(c.background_image) || systemBg) : null
  const colorBg = c.color_background || "#181818"
  const colorText = c.color_text || "#FFFFFF"
  const colorPrimary = c.color_primary || "#FF6B00"

  return (
    <div
      className={`relative w-full aspect-video rounded-xl overflow-hidden border border-bordered ${className}`}
      style={
        bg
          ? undefined
          : { background: `linear-gradient(135deg, ${colorBg} 0%, ${colorPrimary} 140%)` }
      }
    >
      {bg ? (
        <img
          src={bg}
          alt=""
          onError={() => setImgError(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : null}
      {/* faint label-readability scrim over images */}
      {bg ? <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/40 to-transparent" /> : null}
      <span className="sr-only" style={{ color: colorText }} />
    </div>
  )
}
